import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  CheckCircle,
  Eye,
  PaperPlaneTilt,
  PlayCircle,
  Printer,
  Question,
} from '@phosphor-icons/react';
import { MockInterviewPanel } from '../components/MockInterviewPanel';
import { QuestionBankPanel } from '../components/QuestionBankPanel';
import { api, type SectionQA } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  ProgressBar,
  SectionHeading,
  Tabs,
} from '../components/ui';

export function InterviewPrep() {
  const [active, setActive] = useState('bank');

  // Every tab here stands on the course-scoped question bank or the learner's own coursework.
  // The InterviewPackage tabs that used to sit alongside them — Study Guide, STAR Q&A, Project
  // Portfolio — are gone from this screen: no student ever had a package, so they rendered for
  // nobody but the author. The authoring screen in Studio and the packages themselves are
  // untouched; only the dead learner-facing surface is removed.
  const tabs = [
    { id: 'bank', label: 'Question Bank', icon: Question },
    { id: 'mock', label: 'Mock Interview', icon: PaperPlaneTilt },
    { id: 'section', label: 'Section Q&A', icon: Question },
  ];
  const activeTab = tabs.some((t) => t.id === active) ? active : 'bank';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeading
          eyebrow="Interview Prep"
          title="Interview preparation"
          description="Practise interview questions drawn from what you have covered."
        />
        <Link
          to="/questions"
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-teal-700 hover:border-teal-500 hover:bg-white"
        >
          <Printer size={16} weight="bold" /> Print all questions
        </Link>
      </div>

      <Tabs items={tabs} active={activeTab} onChange={setActive} />

      {activeTab === 'bank' && <QuestionBankPanel />}
      {activeTab === 'section' && <SectionQAPanel />}
      {activeTab === 'mock' && <MockInterviewPanel />}
    </div>
  );
}

// ── Section Q&A ──────────────────────────────────────────────────────────────
// Self-study interview practice, one section at a time: read the question, write
// your own answer, then reveal the suggested answer to compare. Not graded.
function SectionQAPanel() {
  const s = useAsync(() => api.sectionQuestions(), []);
  const [session, setSession] = useState<SectionQA | null>(null);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;
  const sections = s.data.filter((sec) => sec.questions.length > 0);

  if (session) return <QASession section={session} onExit={() => setSession(null)} />;

  if (sections.length === 0) {
    return (
      <EmptyState
        icon={Question}
        title="No section Q&A yet"
        description="Interview questions appear here as each part of your course is prepared. Check back as you progress."
      />
    );
  }

  const multiCourse = new Set(sections.map((sec) => sec.courseTitle ?? '')).size > 1;

  return (
    <div className="space-y-4">
      <p className="max-w-[70ch] text-sm leading-relaxed text-slate-600">
        Each set gathers the interview questions for one part of your course. Start a set, answer in your own words,
        then reveal the suggested answer to compare. Nothing here is graded or saved.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((sec) => (
          <Card key={sec.sectionId} className="flex flex-col p-5">
            {multiCourse && sec.courseTitle && <span className="label-mono">{sec.courseTitle}</span>}
            <h3 className="mt-1 text-lg font-bold text-brand-navy">{sec.sectionTitle}</h3>
            <p className="mt-1 text-sm text-slate-500">
              {sec.questions.length} interview question{sec.questions.length === 1 ? '' : 's'}
            </p>
            <div className="mt-5 flex-1" />
            <Button icon={PlayCircle} className="self-start" onClick={() => setSession(sec)}>
              Start Q&amp;A
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

// A guided run through one section's questions: answer, reveal, next.
function QASession({ section, onExit }: { section: SectionQA; onExit: () => void }) {
  const qs = section.questions;
  const total = qs.length;
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  const [done, setDone] = useState(false);

  function restart() {
    setIdx(0);
    setAnswers({});
    setRevealed({});
    setDone(false);
  }

  if (done) {
    return (
      <Card className="flex flex-col items-center gap-4 p-10 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <CheckCircle size={30} weight="fill" />
        </span>
        <div>
          <h3 className="text-xl font-bold text-brand-navy">Nice work</h3>
          <p className="mt-1 text-sm text-slate-500">
            You practised all {total} question{total === 1 ? '' : 's'} for {section.sectionTitle}.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button icon={ArrowsClockwise} onClick={restart}>
            Practise again
          </Button>
          <Button variant="secondary" onClick={onExit}>
            Back to sections
          </Button>
        </div>
      </Card>
    );
  }

  const q = qs[idx];
  const isLast = idx === total - 1;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onExit}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-brand-teal"
        >
          <ArrowLeft size={16} weight="bold" /> All sections
        </button>
        <span className="label-mono">{section.sectionTitle}</span>
      </div>

      <div>
        <div className="mb-1 text-xs font-semibold text-slate-500">
          Question {idx + 1} of {total}
        </div>
        <ProgressBar value={idx + 1} max={total} />
      </div>

      <Card className="space-y-4 p-6">
        <p className="text-lg font-semibold text-brand-navy">{q.question}</p>

        {q.howAsked && (
          <div className="rounded-xl bg-violet-50 p-3">
            <span className="label-mono text-violet-700">What they’re probing for</span>
            <p className="mt-1 text-sm leading-relaxed text-violet-900">{q.howAsked}</p>
          </div>
        )}

        <div>
          <label htmlFor="qa-answer" className="label-mono mb-1 block">
            Your answer (not saved)
          </label>
          <textarea
            id="qa-answer"
            className="input"
            rows={5}
            value={answers[idx] ?? ''}
            onChange={(e) => setAnswers((a) => ({ ...a, [idx]: e.target.value }))}
            placeholder="Say it out loud or jot the key points, then reveal the suggested answer to compare."
          />
        </div>

        {q.answer ? (
          <div>
            <Button
              variant="secondary"
              icon={Eye}
              onClick={() => setRevealed((r) => ({ ...r, [idx]: !r[idx] }))}
              aria-expanded={!!revealed[idx]}
            >
              {revealed[idx] ? 'Hide suggested answer' : 'Reveal suggested answer'}
            </Button>
            {revealed[idx] && (
              <div className="mt-3 whitespace-pre-wrap rounded-xl border border-brand-teal/20 bg-brand-light p-4 text-sm leading-relaxed text-slate-700">
                {q.answer}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No suggested answer for this one — self-assess against what you covered.</p>
        )}
      </Card>

      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" icon={ArrowLeft} disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          Previous
        </Button>
        {isLast ? (
          <Button icon={CheckCircle} onClick={() => setDone(true)}>
            Finish
          </Button>
        ) : (
          <Button icon={ArrowRight} onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Study Guide ──────────────────────────────────────────────────────────────
